import React, { useEffect, useRef, useState } from "react";
import { ActivityCard, MixedText, PlayButton, ProgressPill, shuffled } from "../shared";

/**
 * QuestionMatch — "The Interview Board" for the question-words lesson.
 *
 * Two columns: the six questions in order, and their answers shuffled.
 * Tapping a question selects it (gold ring) and plays it; tapping the
 * answer it asks for locks the pair and plays the answer, while a wrong
 * answer flashes red and the question stays selected. Matched entries
 * become plain divs (a nested PlayButton must never sit inside a
 * disabled button). Completes when every pair is matched.
 */
export default function QuestionMatch({ data, onComplete, audio }) {
  const [answers] = useState(() => shuffled(data.pairs));
  const [selectedId, setSelectedId] = useState(null);
  const [matched, setMatched] = useState(() => new Set());
  const [wrongId, setWrongId] = useState(null);
  const wrongTimer = useRef(null);

  const done = matched.size >= data.pairs.length;

  useEffect(() => {
    if (done) onComplete();
  }, [done, onComplete]);

  useEffect(() => () => clearTimeout(wrongTimer.current), []);

  const handleQuestion = (pair) => {
    setSelectedId(pair.id);
    if (audio) audio.play(pair.question_ar);
  };

  const handleAnswer = (pair) => {
    if (!selectedId) {
      if (audio) audio.play(pair.answer_ar);
      return;
    }
    if (pair.id === selectedId) {
      setMatched((prev) => new Set(prev).add(pair.id));
      setSelectedId(null);
      if (audio) audio.play(pair.answer_ar);
    } else {
      setWrongId(pair.id);
      clearTimeout(wrongTimer.current);
      wrongTimer.current = setTimeout(() => setWrongId(null), 600);
    }
  };

  const entry = (pair, arText, enText, side) => {
    const isMatched = matched.has(pair.id);
    if (isMatched) {
      return (
        <div
          key={pair.id}
          className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl"
          style={{ background: "var(--gold-bg)", border: "1px solid var(--gold-dim)" }}
        >
          <span className="min-w-0">
            <span dir="rtl" lang="ar" className="font-arabic text-xl leading-[1.7] block" style={{ color: "var(--parchment)" }}>
              {arText}
            </span>
            <span className="text-xs block" style={{ color: "var(--parchment-dim)" }}>{enText}</span>
          </span>
          <span className="flex items-center gap-1.5 shrink-0">
            {audio && <PlayButton text={arText} playingText={audio.playingText} onPlay={audio.play} />}
            <span aria-hidden="true" style={{ color: "var(--gold-bright)" }}>✓</span>
          </span>
        </div>
      );
    }
    const isSelected = side === "q" && selectedId === pair.id;
    const isWrong = side === "a" && wrongId === pair.id;
    return (
      <button
        key={pair.id}
        type="button"
        onClick={() => (side === "q" ? handleQuestion(pair) : handleAnswer(pair))}
        aria-pressed={isSelected}
        className="px-3 py-2.5 rounded-xl text-right transition-all hover:brightness-110"
        style={{
          background: "var(--ink)",
          border: isWrong
            ? "1px solid var(--danger, #b04545)"
            : isSelected
              ? "1px solid var(--gold)"
              : "1px solid var(--panel-line)",
          boxShadow: isSelected ? "0 0 0 3px rgba(201,162,39,0.15)" : "none",
        }}
      >
        <span dir="rtl" lang="ar" className="font-arabic text-xl leading-[1.7] block" style={{ color: "var(--parchment)" }}>
          {arText}
        </span>
        <span className="text-xs block text-left" style={{ color: "var(--parchment-dim)" }}>{enText}</span>
      </button>
    );
  };

  return (
    <ActivityCard>
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-sm m-0" style={{ color: "var(--parchment-dim)" }}>
          {selectedId
            ? data.match_prompt || "Now tap the answer it is asking for."
            : data.select_prompt || "Tap a question to hear it."}
        </p>
        <ProgressPill>{matched.size}/{data.pairs.length} matched</ProgressPill>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-2.5">
          {data.pairs.map((pair) => entry(pair, pair.question_ar, pair.question_en, "q"))}
        </div>
        <div className="flex flex-col gap-2.5">
          {answers.map((pair) => entry(pair, pair.answer_ar, pair.answer_en, "a"))}
        </div>
      </div>

      {done && (
        <div
          className="flex items-start gap-2.5 mt-4 px-4 py-3 rounded-xl"
          style={{ background: "var(--violet-bg)", border: "1px solid var(--panel-line)" }}
        >
          <span className="text-lg leading-none" aria-hidden="true">🎤</span>
          <p className="text-sm leading-relaxed m-0" style={{ color: "var(--parchment)" }}>
            <MixedText text={data.takeaway} />
          </p>
        </div>
      )}
    </ActivityCard>
  );
}
